<template>
  <lf-card class="mb-8 max-w-(--breakpoint-md) py-6">
    <loader :is-loading="isLoading" />
    <div class="my-2 space-y-2 px-6">
      <lf-h3>{{ $t("INTEGRATIONS.BRAND") }} {{ $t("COMMON.NAME") }}</lf-h3>
      <span class="mt-2">
        {{ $t("ORGANIZATION.BRAND_NAME") }}
      </span>

      <div class="flex flex-col space-y-2 pt-2">
        <div
          v-for="brand in brandNames"
          :key="brand.id"
          class="border-gray-150 flex items-center justify-between rounded-sm border px-3 py-2"
          data-cy="brand-name-item"
        >
          <span class="text-headline">{{ brand.name }}</span>
          <div class="flex items-center space-x-2">
            <icon-base
              v-tooltip="$t('COMMON.EDIT')"
              :icon="IconEdit"
              class="cursor-pointer text-disabled-medium"
              data-cy="brand-name-edit"
              @click="openModal(brand)"
            />
            <icon-base
              v-tooltip="$t('COMMON.DELETE')"
              :icon="IconDelete"
              class="cursor-pointer text-disabled-medium"
              data-cy="brand-name-delete"
              @click="brandNameToDelete = brand"
            />
          </div>
        </div>

        <div>
          <span
            class="text-md border-gray-150 inline-flex cursor-pointer rounded-sm border px-3 py-2 text-blue-600"
            data-cy="brand-name-add"
            @click="openModal()"
          >
            + {{ $t("COMMON.ADD") }}
          </span>
        </div>
      </div>
    </div>

    <brand-name-modal
      v-if="brandNameBeingEdited !== undefined"
      :brand-name="brandNameBeingEdited"
      @close="closeModal"
      @created="onBrandNameCreated"
      @updated="onBrandNameUpdated"
    />

    <confirm-modal
      v-if="brandNameToDelete"
      :title="$t('COMMON.DELETE')"
      :description="$t('ORGANIZATION.CONFIRM_DELETE_BRAND_NAME')"
      confirm-button-type="error"
      :confirm-button="$t('COMMON.DELETE')"
      :cancel-button="$t('COMMON.CANCEL')"
      :close="() => (brandNameToDelete = null)"
      @confirm="handleDelete"
    />
  </lf-card>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import { AnalyticsEvent } from "@/enums/analytics";
import { useClients } from "@/hooks/clients";
import {
  useClientBrandNamesQuery,
  useDeleteBrandNameMutation,
} from "@/hooks/queries/useBrandNames";
import { useAnalyticsStore } from "@/stores/analytics";

import IconDelete from "@/components/icons/IconDelete.vue";
import IconEdit from "@/components/icons/IconEdit.vue";
import ConfirmModal from "@/components/modals/ConfirmModal.vue";
import BrandNameModal from "@/views/profile/components/BrandNameModal.vue";

import type { BrandName } from "@/schemas/brandName";

const { activeClient } = useClients();
const { trackEvent } = useAnalyticsStore();

const { data: brandNamesResponse, isLoading } = useClientBrandNamesQuery();
const brandNames = computed(() => brandNamesResponse.value?.data ?? []);

const { mutate: deleteBrandName } = useDeleteBrandNameMutation();

const brandNameBeingEdited = ref<BrandName | null | undefined>(undefined);
const brandNameToDelete = ref<BrandName | null>(null);

const openModal = (brand?: BrandName) => {
  brandNameBeingEdited.value = brand ?? null;
};

const closeModal = () => {
  brandNameBeingEdited.value = undefined;
};

const onBrandNameCreated = () => {
  trackEvent(AnalyticsEvent.BrandNameAdded, {
    organization_id: activeClient.value?.id,
  });
};

const onBrandNameUpdated = () => {
  trackEvent(AnalyticsEvent.BrandNameUpdated, {
    organization_id: activeClient.value?.id,
  });
};

const handleDelete = () => {
  if (!brandNameToDelete.value) {
    return;
  }

  deleteBrandName(brandNameToDelete.value.id, {
    onSuccess: () => {
      trackEvent(AnalyticsEvent.BrandNameRemoved, {
        organization_id: activeClient.value?.id,
      });
      brandNameToDelete.value = null;
    },
    onError: () => {
      brandNameToDelete.value = null;
    },
  });
};
</script>
